import { memo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import Help from '../../assets/icons/Help'
import HeadsetIcon from '../../assets/icons/HeadsetIcon'
import BugIcon from '../../assets/icons/BugIcon'
import FeedbackIcon from '../../assets/icons/FeedbackIcon'
import MailIcon from '../../assets/icons/MailIcon'
import ToursTipsIcon from '../../assets/icons/ToursTipsIcon'
import SupportRow from './SupportRow'

export type SupportIntent = 'bug' | 'feedback' | 'tour' | 'help' | 'contact'

type Props = {
    onSelect: (intent: SupportIntent) => void
    /** The current route has a guided tour. */
    tourAvailable: boolean
}

/** First screen of the Help & feedback tab: what does the user want to do? */
const SupportHome = ({ onSelect, tourAvailable }: Props) => (
    <Box sx={{ px: 2, pt: 3, pb: 2 }}>
        <Stack alignItems="center" textAlign="center" sx={{ px: 1, mb: 3 }}>
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    mb: 1.5
                }}
            >
                <HeadsetIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={500} gutterBottom>
                How can we help?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, lineHeight: 1.55 }}>
                Report a problem, share an idea, or find your way around.
            </Typography>
        </Stack>

        <Stack spacing={0.75}>
            <SupportRow
                icon={<BugIcon />}
                title="Report a bug"
                description="Something broke or looks wrong"
                onClick={() => onSelect('bug')}
                data-event="Support Intent Clicked"
                data-event-intent="bug"
            />
            <SupportRow
                icon={<FeedbackIcon />}
                title="Share feedback"
                description="Ideas and feature requests"
                onClick={() => onSelect('feedback')}
                data-event="Support Intent Clicked"
                data-event-intent="feedback"
            />
            <SupportRow
                icon={<ToursTipsIcon />}
                title="Take a tour"
                description={tourAvailable ? 'Walk through the tools on this page' : 'No tour on this page yet'}
                onClick={() => onSelect('tour')}
                disabled={!tourAvailable}
                data-event="Support Intent Clicked"
                data-event-intent="tour"
            />
            <SupportRow
                icon={<Help />}
                title="Help center"
                description="Guides, answers, shortcuts"
                onClick={() => onSelect('help')}
                data-event="Support Intent Clicked"
                data-event-intent="help"
            />
            <SupportRow
                icon={<MailIcon />}
                title="Contact us"
                description="Email the team or find our office"
                onClick={() => onSelect('contact')}
                data-event="Support Intent Clicked"
                data-event-intent="contact"
            />
        </Stack>
    </Box>
)

export default memo(SupportHome)
